/**
 * Intake Assignment Engine
 *
 * Handles patient-to-provider assignments (simulated, in-memory only)
 */

import { ProviderCapacityDay } from '../types/capacity.types';
import {
  IntakePatient,
  PatientAssignment,
  AutoAssignmentResult,
  ProviderRecommendation,
} from '../types/intake.types';
import {
  getProviderRecommendations,
  filterViableRecommendations,
  findAlternativeDates,
  findNextBestProviders,
  generateProvisionalOption,
} from './providerRecommendationEngine';
import { SimulatedVisitBlock } from '../types/simulation.types';
import { formatDateKey } from '../utils/dateHelpers';

interface ProviderWithMetadata {
  providerId: number;
  providerName: string;
  role: string;
  territory: string;
  panelSize: number;
}

/**
 * Assign a single patient to a provider (simulated)
 *
 * @returns Assignment result with simulated intake visit
 */
export function assignPatientToProvider(
  patient: IntakePatient,
  providerId: number,
  providerName: string
): PatientAssignment {
  return {
    patientId: patient.id,
    patientName: patient.patientName,
    providerId,
    providerName,
    intakeVisitDate: patient.intakeVisitDate,
    status: 'success',
  };
}

/**
 * Create simulated intake visit for assignment
 */
export function createIntakeVisit(
  patient: IntakePatient,
  providerId: number
): SimulatedVisitBlock {
  return {
    date: formatDateKey(patient.intakeVisitDate),
    durationMinutes: 60, // Intake visits are always 60 minutes
    providerId,
  };
}

/**
 * Auto-assign all unassigned patients
 *
 * @param patients - List of unassigned patients
 * @param capacitySnapshot - Current capacity data
 * @param providerMetadata - Provider territories and panel sizes
 * @param targetUtilization - Target utilization threshold
 * @returns Auto-assignment results
 */
export function autoAssignPatients(
  patients: IntakePatient[],
  capacitySnapshot: ProviderCapacityDay[],
  providerMetadata: ProviderWithMetadata[],
  targetUtilization: number = 75
): AutoAssignmentResult {
  const assignments: PatientAssignment[] = [];
  let successCount = 0;
  let conflictCount = 0;
  let unassignedCount = 0;

  // Sort patients by priority (HIGH > MEDIUM > LOW), then by intake date
  const sortedPatients = [...patients].sort((a, b) => {
    const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
    const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    return a.intakeVisitDate.getTime() - b.intakeVisitDate.getTime();
  });

  // Track provider utilization changes as we assign
  const assignmentsByProvider = new Map<number, number>(); // providerId -> count of new assignments

  sortedPatients.forEach((patient) => {
    // Get recommendations
    const recommendations = getProviderRecommendations(
      patient,
      capacitySnapshot,
      providerMetadata,
      targetUtilization
    );

    // Filter to only viable options
    const viableRecommendations = filterViableRecommendations(recommendations, false);

    if (viableRecommendations.length === 0) {
      // No viable providers found - generate alternative options
      const alternativeDates = findAlternativeDates(
        patient,
        capacitySnapshot,
        providerMetadata,
        targetUtilization,
        3 // Search ±3 days
      );

      const nextBestProviders = findNextBestProviders(
        patient,
        capacitySnapshot,
        providerMetadata,
        targetUtilization
      );

      const provisionalOption = generateProvisionalOption(patient);

      assignments.push({
        patientId: patient.id,
        patientName: patient.patientName,
        providerId: 0,
        providerName: 'None',
        intakeVisitDate: patient.intakeVisitDate,
        status: 'no_capacity',
        issues: ['No providers available under target utilization on this date. Click for recourse.'],
        alternatives: {
          alternativeDates,
          nextBestProviders,
          provisionalOption,
        },
      });
      unassignedCount++;
      return;
    }

    // Pick best recommendation (accounting for already-assigned patients in this batch)
    let bestRecommendation: ProviderRecommendation | null = null;
    for (const rec of viableRecommendations) {
      const currentAssignments = assignmentsByProvider.get(rec.providerId) || 0;

      // Adjust score based on how many we've already assigned to this provider
      const adjustedScore = rec.score - (currentAssignments * 10);

      if (!bestRecommendation || adjustedScore > bestRecommendation.score) {
        bestRecommendation = { ...rec, score: adjustedScore };
      }
    }

    if (!bestRecommendation) {
      // Shouldn't happen, but handle edge case
      assignments.push({
        patientId: patient.id,
        patientName: patient.patientName,
        providerId: 0,
        providerName: 'None',
        intakeVisitDate: patient.intakeVisitDate,
        status: 'conflict',
        issues: ['Unable to determine best provider'],
      });
      conflictCount++;
      return;
    }

    // Check for potential conflicts
    const issues: string[] = [];
    if (bestRecommendation.availableMinutes < 120) {
      issues.push('Limited capacity on intake date');
    }
    if (bestRecommendation.panelSize > 18) {
      issues.push('Provider has large panel size');
    }

    // Assign
    assignments.push({
      patientId: patient.id,
      patientName: patient.patientName,
      providerId: bestRecommendation.providerId,
      providerName: bestRecommendation.providerName,
      intakeVisitDate: patient.intakeVisitDate,
      status: issues.length > 0 ? 'conflict' : 'success',
      issues: issues.length > 0 ? issues : undefined,
    });

    if (issues.length > 0) {
      conflictCount++;
    } else {
      successCount++;
    }

    // Track assignment
    assignmentsByProvider.set(
      bestRecommendation.providerId,
      (assignmentsByProvider.get(bestRecommendation.providerId) || 0) + 1
    );
  });

  // Generate summary
  const summary = generateAutoAssignSummary(successCount, conflictCount, unassignedCount);

  return {
    assignments,
    successCount,
    conflictCount,
    unassignedCount,
    summary,
  };
}

/**
 * Generate summary text for auto-assignment results
 */
function generateAutoAssignSummary(
  successCount: number,
  conflictCount: number,
  unassignedCount: number
): string {
  const total = successCount + conflictCount + unassignedCount;
  const parts: string[] = [];

  parts.push(`Processed ${total} patients`);

  if (successCount > 0) {
    parts.push(`${successCount} assigned successfully`);
  }

  if (conflictCount > 0) {
    parts.push(`${conflictCount} with potential issues`);
  }

  if (unassignedCount > 0) {
    parts.push(`${unassignedCount} could not be assigned`);
  }

  return parts.join(', ') + '.';
}
